import { SharedMap } from 'fluid-framework';
import { EagerCheckout, Change, initialTree, SharedTree, StablePlace, StableRange, TreeNodeHandle, WriteFormat, } from '@fluid-experimental/tree';
import { waitContainerToCatchUp } from '@fluidframework/container-loader';
import { TypedEventEmitter } from '@fluidframework/common-utils';
function assert(condition, message) {
    if (!condition) {
        throw new Error(`Assertion failed: ${message}`);
    }
}
class AFRSharedTree extends SharedTree {
    static getFactory() {
        return SharedTree.getFactory(WriteFormat.v0_1_1);
    }
}
const schema = {
    initialObjects: {
        hunt: AFRSharedTree,
        guildInfo: SharedMap,
    },
};
export const createNewPuzzlehunt = async (client, guildId, logChannelIds) => createPuzzlehunt(client, (hunt) => {
    hunt.setGuildId(guildId);
    if (logChannelIds) {
        hunt.setLoggingChannelIds(logChannelIds);
    }
});
export async function createNewPuzzlehuntFromExisting(client, existingHunt) {
    assert(existingHunt instanceof Puzzlehunt, 'This API relies on internals of the tree-based Puzzlehunt implementation.');
    return createPuzzlehunt(client, (hunt) => hunt.copyFrom(existingHunt));
}
async function createPuzzlehunt(client, editBeforeAttach) {
    const { container } = await client.createContainer(schema);
    // May want to initialize state here.
    const puzzlehunt = new Puzzlehunt(container);
    editBeforeAttach(puzzlehunt);
    const id = await container.attach();
    const disposer = {
        dispose: () => {
            if (!container.isDirty) {
                container.dispose();
            }
            else {
                console.log(`Delaying close of puzzlehunt ${id} due to unsaved changes.`);
                container.once('saved', () => {
                    console.log(`Completed delayed close of puzzlehunt ${id}.`);
                    container.dispose();
                });
            }
        },
    };
    return { id, puzzlehunt, disposer, container };
}
export const loadExistingPuzzlehunt = async (client, id) => {
    const { container } = await client.getContainer(id, schema);
    await waitContainerToCatchUp(container.container);
    return {
        puzzlehunt: new Puzzlehunt(container),
        disposer: container,
        id,
        container,
    };
};
const puzzleDef = '34edbf0a-7479-4124-ae4f-7091724dc58a';
const roundDef = '81cfc8eb-fd54-4071-b991-fea6db4456d7';
const stringDef = '13574b71-cd65-4672-98bd-e0c045f04f6a';
const numberDef = 'f13ac46f-a39d-4124-a327-abdadcc57f10';
const traitLabels = {};
[
    'name',
    'url',
    'answer',
    'sheetId',
    'children',
    'roleId',
    'channelId',
    'indexChannelId',
    'status',
    'lastStatusUpdate',
].forEach((val) => {
    traitLabels[val] = val;
});
const makeString = (content) => ({
    definition: stringDef,
    payload: content,
});
const makeNumber = (content) => ({
    definition: numberDef,
    payload: content,
});
const makePuzzleBuildNode = (name, url, identifier, discordInfo, sheetId) => {
    let channelId;
    let roleId;
    if (discordInfo !== undefined) {
        channelId = makeString(typeof discordInfo.channel === 'string'
            ? discordInfo.channel
            : discordInfo.channel.id);
        roleId = makeString(typeof discordInfo.role === 'string'
            ? discordInfo.role
            : discordInfo.role.id);
    }
    return {
        definition: puzzleDef,
        identifier,
        traits: {
            [traitLabels.name]: makeString(name),
            [traitLabels.url]: makeString(url),
            [traitLabels.channelId]: channelId,
            [traitLabels.roleId]: roleId,
            [traitLabels.sheetId]: sheetId !== undefined ? makeString(sheetId) : undefined,
        },
    };
};
const makeRoundBuildNode = (name, url, identifier, discordInfo) => {
    let channelId;
    let indexChannelId;
    let roleId;
    if (discordInfo !== undefined) {
        channelId = makeString(typeof discordInfo.channel === 'string'
            ? discordInfo.channel
            : discordInfo.channel.id);
        indexChannelId = makeString(typeof discordInfo.indexChannel === 'string'
            ? discordInfo.indexChannel
            : discordInfo.indexChannel.id);
        roleId = makeString(typeof discordInfo.role === 'string'
            ? discordInfo.role
            : discordInfo.role.id);
    }
    return {
        definition: roundDef,
        identifier,
        traits: {
            [traitLabels.name]: makeString(name),
            [traitLabels.url]: makeString(url),
            [traitLabels.channelId]: channelId,
            [traitLabels.roleId]: roleId,
            [traitLabels.indexChannelId]: indexChannelId,
        },
    };
};
function readCell(trait) {
    assert(trait.length === 1, 'Expected trait to have exactly one property.');
    return trait[0];
}
function readOptional(trait) {
    return !trait || trait.length === 0 ? undefined : readCell(trait);
}
function str(content) {
    assert(typeof content === 'string', 'Content should have been a string.');
    return content;
}
class RoundHandle {
    view;
    id;
    handle;
    traits;
    type = 'round';
    constructor(view, id) {
        this.view = view;
        this.id = id;
        this.handle = new TreeNodeHandle(view, id);
        this.traits = this.handle.traits;
    }
    get name() {
        return str(readCell(this.traits.name).payload);
    }
    get url() {
        return str(readCell(this.traits.url).payload);
    }
    get discordInfo() {
        const roleIdNode = readOptional(this.traits.roleId);
        const channelIdNode = readOptional(this.traits.channelId);
        const indexChannelIdNode = readOptional(this.traits.indexChannelId);
        if (roleIdNode && channelIdNode && indexChannelIdNode) {
            return {
                roleId: str(roleIdNode.payload),
                channelId: str(channelIdNode.payload),
                indexChannelId: str(indexChannelIdNode.payload),
            };
        }
        return undefined;
    }
    get roundId() {
        const parentId = this.handle.node.parentage.parent;
        const grandparent = this.view.tryGetParentViewNode(parentId);
        // grandparent === undefined implies parent is the root node. So this round has no parent.
        return grandparent === undefined ? undefined : parentId;
    }
    get children() {
        return Array.from(this.handle.node.traits.get(traitLabels.children) ?? [], (childId) => {
            const childHandle = getHandle(this.view, childId);
            assert(childHandle.type === 'puzzle' ||
                childHandle.type === 'round', `Round should only have puzzle and round children but had a ${childHandle.type}.`);
            return childHandle;
        });
    }
}
class StringHandle {
    view;
    id;
    handle;
    traits;
    type = 'string';
    constructor(view, id) {
        this.view = view;
        this.id = id;
        this.handle = new TreeNodeHandle(view, id);
    }
    get value() {
        return this.handle.payload;
    }
    get parentage() {
        return this.handle.node.parentage;
    }
}
class NumberHandle {
    view;
    id;
    handle;
    traits;
    type = 'number';
    constructor(view, id) {
        this.view = view;
        this.id = id;
        this.handle = new TreeNodeHandle(view, id);
    }
    get value() {
        return this.handle.payload;
    }
    get parentage() {
        return this.handle.node.parentage;
    }
}
class PuzzleHandle {
    id;
    handle;
    traits;
    type = 'puzzle';
    constructor(view, id) {
        this.id = id;
        this.handle = new TreeNodeHandle(view, id);
        this.traits = this.handle.traits;
    }
    get name() {
        return str(readCell(this.traits.name).payload);
    }
    get url() {
        return str(readCell(this.traits.url).payload);
    }
    get discordInfo() {
        const roleIdNode = readOptional(this.traits.roleId);
        const channelIdNode = readOptional(this.traits.channelId);
        if (roleIdNode && channelIdNode) {
            return {
                roleId: str(roleIdNode.payload),
                channelId: str(channelIdNode.payload),
            };
        }
        return undefined;
    }
    get roundId() {
        return this.handle.node.parentage.parent;
    }
    get sheetId() {
        return readOptional(this.traits.sheetId)?.payload;
    }
    get answer() {
        return readOptional(this.traits.answer)?.payload;
    }
    get status() {
        return readOptional(this.traits.status)?.payload;
    }
    get lastStatusUpdate() {
        return readOptional(this.traits.lastStatusUpdate)?.payload;
    }
}
function replace(content, location) {
    return [
        Change.delete(StableRange.all(location)),
        ...Change.insertTree(content, StablePlace.atEndOf(location)),
    ];
}
const getHandle = (view, id) => {
    const viewNode = view.getViewNode(id);
    switch (viewNode.definition) {
        case roundDef:
            return new RoundHandle(view, id);
        case puzzleDef:
            return new PuzzleHandle(view, id);
        case stringDef:
            return new StringHandle(view, id);
        case numberDef:
            return new NumberHandle(view, id);
    }
};
class Puzzlehunt extends TypedEventEmitter {
    hunt;
    checkout;
    guildInfoMap;
    constructor(container) {
        super();
        const { hunt, guildInfo } = container.initialObjects;
        this.hunt = hunt;
        this.checkout = new EagerCheckout(hunt);
        this.hunt.on('error', (err) => console.log('SharedTree error:', err));
        this.checkout.on('error', (err) => console.log('EagerCheckout error:', err));
        this.checkout.on('viewChange', (before, after) => {
            this.emit('viewChange', before, after, getHandle);
        });
        this.guildInfoMap = guildInfo;
    }
    setGuildId(id) {
        this.guildInfoMap.set('id', id);
    }
    get guildId() {
        return this.guildInfoMap.get('id');
    }
    get loggingChannelIds() {
        return this.guildInfoMap.get('logChannelIds');
    }
    setLoggingChannelIds(ids) {
        this.guildInfoMap.set('logChannelIds', ids);
    }
    addPuzzle(name, url, round) {
        const id = this.hunt.generateNodeId();
        this.checkout.applyEdit(...Change.insertTree(makePuzzleBuildNode(name, url, id), StablePlace.atEndOf({
            parent: round.id,
            label: 'children',
        })));
        return new PuzzleHandle(this.checkout.currentView, id);
    }
    addPuzzles(puzzles, round) {
        this.checkout.applyEdit(...Change.insertTree(puzzles.map(({ name, url }) => makePuzzleBuildNode(name, url)), StablePlace.atEndOf({
            parent: round.id,
            label: 'children',
        })));
    }
    addRound(name, url, parentRound) {
        const id = this.hunt.generateNodeId();
        const parentNodeId = parentRound?.id ??
            this.hunt.convertToNodeId(initialTree.identifier);
        this.checkout.applyEdit(...Change.insertTree(makeRoundBuildNode(name, url, id), StablePlace.atEndOf({
            parent: parentNodeId,
            label: 'children',
        })));
        return new RoundHandle(this.checkout.currentView, id);
    }
    addRounds(rounds, parentRound) {
        this.checkout.applyEdit(...Change.insertTree(rounds.map(({ name, url }) => makeRoundBuildNode(name, url)), StablePlace.atEndOf({
            parent: parentRound.id,
            label: 'children',
        })));
    }
    delete(puzzleObj) {
        this.checkout.applyEdit(Change.delete(StableRange.only(puzzleObj.id)));
    }
    changeName(puzzleObj, name) {
        this.checkout.applyEdit(...replace(makeString(name), {
            parent: puzzleObj.id,
            label: traitLabels.name,
        }));
    }
    changeUrl(puzzleObj, url) {
        this.checkout.applyEdit(...replace(makeString(url), {
            parent: puzzleObj.id,
            label: traitLabels.url,
        }));
    }
    solve(id, answer) {
        const answerTraitLocation = { parent: id, label: traitLabels.answer };
        this.checkout.applyEdit(...replace(makeString(answer), answerTraitLocation));
        return new PuzzleHandle(this.checkout.currentView, id);
    }
    updateStatus(puzzle, status) {
        this.checkout.applyEdit(...replace(makeString(status), {
            parent: puzzle.id,
            label: traitLabels.status,
        }), ...replace(makeNumber(Date.now()), {
            parent: puzzle.id,
            label: traitLabels.lastStatusUpdate,
        }));
    }
    clearStatus(puzzle) {
        this.checkout.applyEdit(Change.delete(StableRange.all({
            parent: puzzle.id,
            label: traitLabels.status,
        })), Change.delete(StableRange.all({
            parent: puzzle.id,
            label: traitLabels.lastStatusUpdate,
        })));
    }
    move(obj, newParent) {
        this.checkout.applyEdit(...Change.move(StableRange.only(obj.id), StablePlace.atEndOf({
            parent: newParent.id,
            label: traitLabels.children,
        })));
    }
    augmentWithDiscord(obj, discordInfo) {
        const { channel, role, indexChannel } = discordInfo;
        const channelId = typeof channel === 'string' ? channel : channel.id;
        const roleId = typeof role === 'string' ? role : role.id;
        this.checkout.openEdit();
        this.checkout.applyChanges(...replace(makeString(channelId), {
            parent: obj.id,
            label: traitLabels.channelId,
        }));
        this.checkout.applyChanges(...replace(makeString(roleId), {
            parent: obj.id,
            label: traitLabels.roleId,
        }));
        if (indexChannel !== undefined) {
            const indexChannelId = typeof indexChannel === 'string'
                ? indexChannel
                : indexChannel.id;
            this.checkout.applyChanges(...replace(makeString(indexChannelId), {
                parent: obj.id,
                label: traitLabels.indexChannelId,
            }));
        }
        this.checkout.closeEdit();
    }
    augmentWithGoogleSheet(puzzle, sheetId) {
        this.checkout.applyEdit(...replace(makeString(sheetId), {
            parent: puzzle.id,
            label: traitLabels.sheetId,
        }));
    }
    getRound(id) {
        return new RoundHandle(this.checkout.currentView, id);
    }
    getPuzzle(id) {
        return new PuzzleHandle(this.checkout.currentView, id);
    }
    get rounds() {
        return Array.from(this.getAllDefsMatching(this.checkout.currentView.root, roundDef), (id) => new RoundHandle(this.checkout.currentView, id));
    }
    get puzzles() {
        return Array.from(this.getAllDefsMatching(this.checkout.currentView.root, puzzleDef), (id) => new PuzzleHandle(this.checkout.currentView, id));
    }
    *getAllDefsMatching(id, def) {
        const viewNode = this.checkout.currentView.getViewNode(id);
        if (viewNode.definition === def) {
            yield viewNode.identifier;
        }
        for (const child of viewNode.traits.get(traitLabels.children) ?? []) {
            yield* this.getAllDefsMatching(child, def);
        }
    }
    copyFrom(other) {
        this.setGuildId(other.guildId);
        this.setLoggingChannelIds(other.loggingChannelIds);
        const buildNodeFromHandle = (handle) => {
            const traits = {};
            for (const [key, value] of Object.entries(handle.traits)) {
                traits[key] = Array.from(value, buildNodeFromHandle);
            }
            return {
                definition: handle.definition,
                payload: handle.payload,
                traits,
            };
        };
        const { currentView } = other.hunt;
        const rootHandle = new TreeNodeHandle(currentView, currentView.root);
        const roundNodes = rootHandle.traits['children'].map(buildNodeFromHandle);
        const parentNodeId = this.hunt.convertToNodeId(initialTree.identifier);
        this.checkout.applyEdit(...Change.insertTree(roundNodes, StablePlace.atEndOf({
            parent: parentNodeId,
            label: 'children',
        })));
    }
}
//# sourceMappingURL=puzzlehunt.js.map